import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types/content';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  const goTo = (next: number) => {
    setIndex((next + movies.length) % movies.length);
  };

  const active = movies[index];

  return (
    <section className="relative h-[440px] w-full overflow-hidden bg-foreground-950 md:h-[600px]">
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title}剧照`}
            title={`${movie.title} - 红杏影视`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-6xl flex-col justify-center px-4 md:px-8">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              {active.type}
            </span>
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-foreground-950">
              评分 {active.rating.toFixed(1)}
            </span>
            <span className="text-xs text-background-100/80">
              {active.year} · {active.region} · {active.genres.join(' / ')}
            </span>
          </div>
          <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h2>
          <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
            {active.synopsis}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-lg" />
              查看作品详情
            </Link>
            <Link
              to="/category"
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 bg-background-50/10 px-6 py-3 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              浏览全部影视
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute right-4 top-1/2 z-20 hidden -translate-y-1/2 flex-col gap-3 md:flex md:right-8">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-100/30 bg-foreground-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-500"
        >
          <i className="ri-arrow-up-line" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-100/30 bg-foreground-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-500"
        >
          <i className="ri-arrow-down-line" />
        </button>
      </div>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 md:left-8 md:translate-x-0">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到${movie.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-100/50 hover:bg-background-100'
            }`}
          />
        ))}
      </div>
    </section>
  );
}